/** Typed settings registry. All admin-configurable knobs live here. */

import { defaultStateRates } from "./shipping.js";
import { DEFAULT_ORDER_NUMBER_TEMPLATE } from "./order-number.js";
import { EMAIL_LAYOUT_KEYS, EMAIL_TEMPLATES } from "./email-templates.js";
import { DEFAULT_FLEXXI_REPUTATION_WEIGHTS, DEFAULT_FLEXXI_SLOT_TIERS } from "./flexxi.js";

export type SettingType = "string" | "number" | "boolean" | "json" | "secret";

export interface SettingDef {
  key: string;
  group: string;
  label: string;
  type: SettingType;
  defaultValue: unknown;
  description?: string;
}

export const SETTING_DEFS: SettingDef[] = [
  // ---- General ----
  { key: "general.store_name", group: "general", label: "Store name", type: "string", defaultValue: "Mr Gaole" },
  { key: "general.support_email", group: "general", label: "Support email", type: "string", defaultValue: "support@mrgaole.com" },
  { key: "general.base_currency", group: "general", label: "Base currency", type: "string", defaultValue: "MYR" },

  // ---- Storefront ----
  {
    key: "reviews.shop_enabled",
    group: "storefront",
    label: "Shop product reviews",
    type: "boolean",
    defaultValue: false,
    description:
      "When on, customers can rate and review Mr Gaole shop products. Marketplace reviews are always on and unaffected.",
  },
  {
    key: "vault.buy_to_vault_enabled",
    group: "storefront",
    label: "Buy to Vault at checkout",
    type: "boolean",
    defaultValue: true,
    description:
      "Lets customers store purchased items in their vault instead of shipping now. Requires the Collection module.",
  },
  {
    key: "social.whatsapp_url",
    group: "storefront",
    label: "WhatsApp community link",
    type: "string",
    defaultValue: "https://chat.whatsapp.com/Fi5OoMkTe4X6cLsbMYZhHG",
    description: "Shown in the footer and the floating social button. Leave empty to hide.",
  },
  {
    key: "social.instagram_url",
    group: "storefront",
    label: "Instagram profile link",
    type: "string",
    defaultValue: "https://www.instagram.com/mrgaole",
    description: "Leave empty to hide.",
  },
  {
    key: "social.facebook_url",
    group: "storefront",
    label: "Facebook page link",
    type: "string",
    defaultValue: "https://www.facebook.com/share/1JRYqPQq87/",
    description: "Leave empty to hide.",
  },
  {
    key: "social.floating_button_enabled",
    group: "storefront",
    label: "Floating social button",
    type: "boolean",
    defaultValue: true,
    description: "Bottom-right button that expands to WhatsApp, Instagram and Facebook. Hidden on checkout pages.",
  },

  // ---- Inventory ----
  {
    key: "inventory.allow_negative_stock",
    group: "inventory",
    label: "Allow negative stock",
    type: "boolean",
    defaultValue: false,
    description: "When enabled, checkout, raffles, and stock adjustments may exceed available on-hand quantity.",
  },

  // ---- Orders ----
  {
    key: "orders.number_template",
    group: "orders",
    label: "Order number template",
    type: "string",
    defaultValue: DEFAULT_ORDER_NUMBER_TEMPLATE,
    description:
      "Formula for new order numbers. Use {YYYY}, {YY}, {MM}, {DD} for date parts and {seq} or {seq:N} for an incremental counter. Date tokens control when the counter resets.",
  },

  // ---- SMTP ----
  { key: "smtp.host", group: "smtp", label: "SMTP host", type: "string", defaultValue: "" },
  { key: "smtp.port", group: "smtp", label: "SMTP port", type: "number", defaultValue: 587 },
  { key: "smtp.secure", group: "smtp", label: "Use TLS (465)", type: "boolean", defaultValue: false },
  { key: "smtp.user", group: "smtp", label: "SMTP username", type: "string", defaultValue: "" },
  { key: "smtp.pass", group: "smtp", label: "SMTP password", type: "secret", defaultValue: "" },
  { key: "smtp.from", group: "smtp", label: "From address", type: "string", defaultValue: "Mr Gaole <no-reply@mrgaole.com>" },
  { key: "smtp.enabled", group: "smtp", label: "Email sending enabled", type: "boolean", defaultValue: false },

  // ---- Email templates (edited via Settings → Email templates) ----
  {
    key: EMAIL_LAYOUT_KEYS.footerText,
    group: "email",
    label: "Email footer text",
    type: "string",
    defaultValue: "Mr Gaole — Connecting Passion Through Collectibles",
  },
  {
    key: EMAIL_LAYOUT_KEYS.primaryColor,
    group: "email",
    label: "Email primary color",
    type: "string",
    defaultValue: "#5460ED",
    description: "Used for CTA buttons and accents in transactional emails.",
  },
  {
    key: EMAIL_LAYOUT_KEYS.accentColor,
    group: "email",
    label: "Email accent color",
    type: "string",
    defaultValue: "#DD54EB",
    description: "Header accent in transactional emails.",
  },
  ...EMAIL_TEMPLATES.flatMap((t) => [
    {
      key: t.subjectKey,
      group: "email" as const,
      label: `${t.title} — subject`,
      type: "string" as const,
      defaultValue: t.defaultSubject,
    },
    {
      key: t.bodyKey,
      group: "email" as const,
      label: `${t.title} — body`,
      type: "string" as const,
      defaultValue: t.defaultBody,
      description: "Markdown. Use token placeholders listed in the template editor.",
    },
  ]),

  // ---- Payments ----
  { key: "payments.stripe.enabled", group: "payments", label: "Stripe enabled", type: "boolean", defaultValue: false },
  { key: "payments.stripe.secret_key", group: "payments", label: "Stripe secret key", type: "secret", defaultValue: "" },
  { key: "payments.stripe.webhook_secret", group: "payments", label: "Stripe webhook secret", type: "secret", defaultValue: "" },
  { key: "payments.gkash.enabled", group: "payments", label: "Gkash enabled", type: "boolean", defaultValue: false },
  { key: "payments.gkash.merchant_id", group: "payments", label: "Gkash merchant ID (MID)", type: "string", defaultValue: "" },
  { key: "payments.gkash.signature_key", group: "payments", label: "Gkash signature key", type: "secret", defaultValue: "" },
  {
    key: "payments.gkash.base_url",
    group: "payments",
    label: "Gkash payment URL",
    type: "string",
    defaultValue: "https://api.gkash.my/api/PaymentForm.aspx",
    description:
      "Production: api.gkash.my/api/PaymentForm.aspx · Staging: api-staging.pay.asia/api/PaymentForm.aspx (not /api/payment/form)",
  },
  { key: "payments.bank_in.enabled", group: "payments", label: "Manual bank-in enabled", type: "boolean", defaultValue: true },
  { key: "payments.bank_in.instructions", group: "payments", label: "Bank-in instructions", type: "string", defaultValue: "Transfer to Maybank 1234567890 (MR GAOLE SDN BHD) and upload your receipt." },
  {
    key: "payments.bank_in.bank",
    group: "payments",
    label: "Bank",
    type: "string",
    defaultValue: "Maybank",
    description: "Bank name shown with a copy button during manual bank transfer.",
  },
  {
    key: "payments.bank_in.account_number",
    group: "payments",
    label: "Bank account number",
    type: "string",
    defaultValue: "1234567890",
    description: "Shown with a copy button during manual bank transfer.",
  },
  {
    key: "payments.bank_in.account_name",
    group: "payments",
    label: "Bank account name",
    type: "string",
    defaultValue: "MR GAOLE SDN BHD",
    description: "Beneficiary name shown with a copy button during manual bank transfer.",
  },
  {
    key: "payments.bank_in.qr_image_url",
    group: "payments",
    label: "Bank-in QR image",
    type: "string",
    defaultValue: "",
    description: "Optional DuitNow or bank QR code. Customers can view and download it during manual bank transfer.",
  },

  // ---- Credits ----
  { key: "credits.conversion_rate", group: "credits", label: "Credits per 1 MYR", type: "number", defaultValue: 1 },

  // ---- Raffles ----
  { key: "raffles.payment_deadline_hours", group: "raffles", label: "Winner payment deadline (hours)", type: "number", defaultValue: 24 },
  { key: "raffles.default_entry_window_minutes", group: "raffles", label: "Default entry window (minutes)", type: "number", defaultValue: 60 },
  { key: "raffles.redraw_on_forfeit", group: "raffles", label: "Re-draw when winner forfeits", type: "boolean", defaultValue: true },
  { key: "raffles.max_tickets_per_user", group: "raffles", label: "Tickets per user", type: "number", defaultValue: 1 },

  // ---- Auctions ----
  { key: "auctions.anti_snipe_minutes", group: "auctions", label: "Anti-snipe extension (minutes)", type: "number", defaultValue: 2 },
  { key: "auctions.payment_deadline_hours", group: "auctions", label: "Winner payment deadline (hours)", type: "number", defaultValue: 24 },

  // ---- Shipping ----
  {
    key: "shipping.flat_fee",
    group: "shipping",
    label: "Default fee for other countries",
    type: "number",
    defaultValue: 10,
    description: "Used for international addresses when the country is not listed, and as the Malaysia state fallback.",
  },
  { key: "shipping.free_above", group: "shipping", label: "Free shipping above (0 = never)", type: "number", defaultValue: 0 },
  {
    key: "shipping.country_rates",
    group: "shipping",
    label: "Per-country delivery fees",
    type: "json",
    defaultValue: [],
    description: "Flat fee per non-Malaysia country. Unlisted countries use the default fee above.",
  },
  {
    key: "shipping.state_rates",
    group: "shipping",
    label: "Per-state delivery fees (Malaysia)",
    type: "json",
    defaultValue: defaultStateRates(10),
    description: "One flat fee per Malaysian state. Edit in the rate table below.",
  },
  {
    key: "shipping.advanced",
    group: "shipping",
    label: "Advanced shipping surcharges",
    type: "json",
    defaultValue: { enabled: false, rules: [] },
    description: "Optional stackable surcharge rules on top of country/state base fees. Edited in Utils → Shipping.",
  },
  { key: "shipping.sender.name", group: "shipping", label: "Sender name", type: "string", defaultValue: "Mr Gaole" },
  { key: "shipping.sender.phone", group: "shipping", label: "Sender phone", type: "string", defaultValue: "" },
  { key: "shipping.sender.address1", group: "shipping", label: "Sender address line 1", type: "string", defaultValue: "" },
  { key: "shipping.sender.address2", group: "shipping", label: "Sender address line 2", type: "string", defaultValue: "" },
  { key: "shipping.sender.postcode", group: "shipping", label: "Sender postcode", type: "string", defaultValue: "" },
  { key: "shipping.sender.city", group: "shipping", label: "Sender city", type: "string", defaultValue: "" },
  { key: "shipping.sender.state", group: "shipping", label: "Sender state", type: "string", defaultValue: "" },
  { key: "shipping.sender.country", group: "shipping", label: "Sender country", type: "string", defaultValue: "MY" },
  { key: "shipping.default_hs_code", group: "shipping", label: "Default HS code (EasyParcel)", type: "string", defaultValue: "" },
  { key: "shipping.default_item_origin", group: "shipping", label: "Default item country of origin (EasyParcel)", type: "string", defaultValue: "" },
  { key: "shipping.default_residential", group: "shipping", label: "Residential address default (Yes/No, EasyParcel)", type: "string", defaultValue: "" },
  {
    key: "shipping.pickup.enabled",
    group: "shipping",
    label: "Enable store pickup",
    type: "boolean",
    defaultValue: false,
    description: "Allow customers to collect orders at a configured location instead of delivery.",
  },
  {
    key: "shipping.pickup.min_lead_hours",
    group: "shipping",
    label: "Pickup minimum lead time (hours)",
    type: "number",
    defaultValue: 24,
  },
  {
    key: "shipping.pickup.max_days_ahead",
    group: "shipping",
    label: "Pickup max days ahead",
    type: "number",
    defaultValue: 14,
  },
  {
    key: "shipping.pickup.locations",
    group: "shipping",
    label: "Pickup locations",
    type: "json",
    defaultValue: [],
    description: "Store pickup locations. Edited in Utils → Shipping → Pickup.",
  },

  // ---- Company / invoicing ----
  { key: "company.legal_name", group: "company", label: "Legal name", type: "string", defaultValue: "MR GAOLE SDN BHD" },
  { key: "company.reg_no", group: "company", label: "Registration no.", type: "string", defaultValue: "" },
  { key: "company.tax_id", group: "company", label: "Tax registration no. (SST/GST)", type: "string", defaultValue: "", description: "Shown on invoice/receipt PDF header when set." },
  { key: "company.phone", group: "company", label: "Phone", type: "string", defaultValue: "" },
  {
    key: "company.whatsapp",
    group: "company",
    label: "WhatsApp number",
    type: "string",
    defaultValue: "",
    description: "International digits only (e.g. 60123456789). Shows a chat button on the storefront home page.",
  },
  { key: "company.email", group: "company", label: "Email", type: "string", defaultValue: "support@mrgaole.com" },
  { key: "company.address1", group: "company", label: "Address line 1", type: "string", defaultValue: "" },
  { key: "company.address2", group: "company", label: "Address line 2", type: "string", defaultValue: "" },
  { key: "company.postcode", group: "company", label: "Postcode", type: "string", defaultValue: "" },
  { key: "company.city", group: "company", label: "City", type: "string", defaultValue: "" },
  { key: "company.state", group: "company", label: "State", type: "string", defaultValue: "" },
  { key: "company.country", group: "company", label: "Country", type: "string", defaultValue: "MY" },
  {
    key: "company.logo_file_key",
    group: "company",
    label: "Logo file key",
    type: "string",
    defaultValue: "",
    description: "S3 object key for company logo on invoices/receipts.",
  },
  {
    key: "company.tax_rate",
    group: "company",
    label: "Tax rate (%)",
    type: "number",
    defaultValue: 0,
    description: "Percentage applied on document totals (e.g. 6 for 6% SST).",
  },
  {
    key: "company.tax_label",
    group: "company",
    label: "Tax label on documents",
    type: "string",
    defaultValue: "SST",
    description: "Label used in the tax row on invoices and receipts (e.g. SST, GST).",
  },
  {
    key: "company.tax_inclusive",
    group: "company",
    label: "Prices include tax",
    type: "boolean",
    defaultValue: false,
    description: "When enabled, order line prices are treated as tax-inclusive.",
  },
  {
    key: "company.invoice_footer",
    group: "company",
    label: "Document footer",
    type: "string",
    defaultValue: "",
    description: "Optional footer text on invoices and receipts.",
  },

  // ---- Card uploader ----
  {
    key: "carduploader.model_id",
    group: "carduploader",
    label: "Vision model for identification",
    type: "string",
    defaultValue: "",
    description: "Choose a Vision model from Admin → Models & Knowledge. Leave empty to use the default Vision model.",
  },
  { key: "carduploader.title_template", group: "carduploader", label: "Product title template", type: "string", defaultValue: "{name} - {set} {number} {variant}" },
  { key: "carduploader.default_condition", group: "carduploader", label: "Default condition", type: "string", defaultValue: "Near Mint" },
  { key: "carduploader.auto_publish_confidence", group: "carduploader", label: "Auto-approve confidence (0-1, 0 = off)", type: "number", defaultValue: 0 },
  { key: "carduploader.price_markup_pct", group: "carduploader", label: "Price markup % on AI estimate", type: "number", defaultValue: 0 },
  {
    key: "carduploader.category_map",
    group: "carduploader",
    label: "Game → category slug map",
    type: "json",
    defaultValue: [
      { game: "Pokemon", language: "JP", parentSlug: "pokemon-japan" },
      { game: "Pokemon", parentSlug: "pokemon" },
      { game: "One Piece", parentSlug: "one-piece" },
    ],
    description: "Maps detected game/language to a parent category slug. Leaf categories (Raw, PSA-9, etc.) are nested under the parent.",
  },

  // ---- Packs ----
  {
    key: "packs.artwork_model_id",
    group: "packs",
    label: "Artwork model for pack images",
    type: "string",
    defaultValue: "",
    description: "Choose an Image Gen model from Admin → Models & Knowledge. Used when staff generate pack artwork in Backoffice → Packs.",
  },

  // ---- Security ----
  {
    key: "security.mfa_email_enabled",
    group: "security",
    label: "2FA via email",
    type: "boolean",
    defaultValue: false,
    description: "Allow users to enable 2FA via email OTP. Configure SMTP in Email Settings first.",
  },
  {
    key: "security.mfa_totp_enabled",
    group: "security",
    label: "2FA via authenticator (TOTP)",
    type: "boolean",
    defaultValue: false,
    description: "Allow users to enable authenticator app 2FA.",
  },
  {
    key: "security.pin_enabled",
    group: "security",
    label: "Enable PIN login",
    type: "boolean",
    defaultValue: false,
    description: "When enabled, users can set a 6-digit PIN to re-login after session timeout or lock.",
  },
  {
    key: "security.pin_setup_notify",
    group: "security",
    label: "Show PIN setup notification",
    type: "boolean",
    defaultValue: false,
    description: "When PIN login is enabled, prompt users without a PIN to set one.",
  },
  {
    key: "security.session_timeout_min",
    group: "security",
    label: "Session timeout (minutes)",
    type: "number",
    defaultValue: 0,
    description: "Auto-lock after inactivity. 0 = never. If PIN is enabled, users can unlock with PIN.",
  },
  {
    key: "security.refresh_token_ttl_days",
    group: "security",
    label: "Refresh session lifetime (days)",
    type: "number",
    defaultValue: 0,
    description: "How long refresh tokens remain valid. 0 = platform default (30 days).",
  },
  {
    key: "security.sms_otp_enabled",
    group: "security",
    label: "Require SMS phone verification",
    type: "boolean",
    defaultValue: false,
    description: "When enabled, storefront customers must verify a mobile number via SMS OTP before using the app.",
  },
  {
    key: "security.sms_otp_api_username",
    group: "security",
    label: "OneWaySMS API username",
    type: "string",
    defaultValue: "",
    description: "API username from your OneWaySMS account (not your web login username).",
  },
  {
    key: "security.sms_otp_api_password",
    group: "security",
    label: "OneWaySMS API password",
    type: "secret",
    defaultValue: "",
    description: "API password from your OneWaySMS account.",
  },
  {
    key: "security.sms_otp_brand_name",
    group: "security",
    label: "SMS brand name",
    type: "string",
    defaultValue: "MrGaole",
    description: "Registered brand or company name for OneWaySMS (exact match). Used as RM0.00 {brand}:…",
  },

  // ---- Legal pages (markdown) ----
  {
    key: "legal.terms",
    group: "legal",
    label: "Terms & Conditions",
    type: "string",
    defaultValue: `# Terms & Conditions

Welcome to Mr Gaole. By using our platform you agree to these terms.

## Use of the platform

- You must provide accurate account information.
- You are responsible for activity under your account.
- We may suspend accounts that violate these terms.

## Purchases

All sales are subject to product availability and our Refund Policy.

## Contact

Questions? Email support@mrgaole.com.`,
    description: "Markdown supported",
  },
  {
    key: "legal.privacy",
    group: "legal",
    label: "Privacy Policy",
    type: "string",
    defaultValue: `# Privacy Policy

We respect your privacy and handle personal data in line with applicable laws.

## Data we collect

- Account details (email, display name)
- Order and payment records
- Usage data needed to operate the service

## How we use data

- To fulfil orders and provide support
- To improve our products and prevent fraud

## Your rights

Contact us at support@mrgaole.com to request access, correction, or deletion of your data where applicable.`,
    description: "Markdown supported",
  },
  {
    key: "legal.refund",
    group: "legal",
    label: "Refund Policy",
    type: "string",
    defaultValue: `# Refund Policy

## Eligible refunds

- Defective or incorrect items received
- Orders cancelled before fulfilment (where applicable)

## Non-refundable

- Opened digital pack pulls unless required by law
- Raffle and auction wins after payment deadline forfeiture

## How to request

Open a support case from your account or email support@mrgaole.com with your order number.`,
    description: "Markdown supported",
  },
  {
    key: "legal.shipping",
    group: "legal",
    label: "Shipping Policy",
    type: "string",
    defaultValue: `# Shipping Policy

## Delivery areas

We ship within Malaysia unless stated otherwise on the product page.

## Processing time

Orders are typically packed within 1–3 business days after payment confirmation.

## Shipping fees

Flat delivery fees may apply. Free shipping thresholds are shown at checkout when available.

## Tracking

You will receive tracking details by email once your order ships.`,
    description: "Markdown supported",
  },

  // ---- CMS (edited via Content → Slider) ----
  {
    key: "cms.hero_fallback.eyebrow",
    group: "cms",
    label: "Hero fallback eyebrow",
    type: "string",
    defaultValue: "Mr Gaole",
    description: "Small label on the default home banner when no carousel slides are active.",
  },
  {
    key: "cms.hero_fallback.headline",
    group: "cms",
    label: "Hero fallback headline",
    type: "string",
    defaultValue: "Connecting passion through collectibles",
    description: "Main heading on the default home banner.",
  },
  {
    key: "cms.hero_fallback.description",
    group: "cms",
    label: "Hero fallback description",
    type: "string",
    defaultValue:
      "Rip packs, win raffles, snipe auctions and trade the cards you love — all in one place.",
    description: "Supporting copy on the default home banner.",
  },

  // ---- Marketplace (C2C) ----
  {
    key: "marketplace.enabled",
    group: "marketplace",
    label: "Marketplace enabled",
    type: "boolean",
    defaultValue: false,
    description: "When off, marketplace routes return 404 and nav is hidden.",
  },
  {
    key: "marketplace.default_platform_fee_percent",
    group: "marketplace",
    label: "Default platform fee (%)",
    type: "number",
    defaultValue: 5,
    description: "Commission on item subtotal per seller order unless overridden on seller profile.",
  },
  {
    key: "marketplace.kyc_required",
    group: "marketplace",
    label: "KYC required before selling",
    type: "boolean",
    defaultValue: true,
  },
  {
    key: "marketplace.auto_confirm_days",
    group: "marketplace",
    label: "Auto-confirm receipt (days)",
    type: "number",
    defaultValue: 7,
    description: "After seller marks complete, buyer order auto-confirms if buyer does not act.",
  },
  {
    key: "marketplace.listing_requires_moderation",
    group: "marketplace",
    label: "Listing moderation",
    type: "boolean",
    defaultValue: false,
    description: "New listings go to PENDING_REVIEW until staff approves.",
  },
  {
    key: "marketplace.collect_label",
    group: "marketplace",
    label: "Collect option label",
    type: "string",
    defaultValue: "COD",
    description:
      "Customer- and seller-facing name for the marketplace in-person handover option (e.g. COD). Mr Gaole store pickup is unaffected.",
  },

  // ---- FlexxiPlan ----
  {
    key: "flexxi.enabled",
    group: "flexxi",
    label: "FlexxiPlan enabled",
    type: "boolean",
    defaultValue: false,
    description: "Requires vault module. When off, instalment checkout and APIs are hidden.",
  },
  {
    key: "flexxi.marketplace_enabled",
    group: "flexxi",
    label: "Allow FlexxiPlan on marketplace listings",
    type: "boolean",
    defaultValue: false,
    description: "When off, FlexxiPlan is only offered on products sold directly by Mr Gaole. Existing plans continue.",
  },
  {
    key: "flexxi.kyc_required",
    group: "flexxi",
    label: "Require customer KYC for FlexxiPlan",
    type: "boolean",
    defaultValue: true,
    description:
      "When on, customers must complete identity verification (staff-approved) before quote, deposit, or plan payments. Active sellers with approved seller KYC may skip.",
  },
  {
    key: "flexxi.eligible_mode",
    group: "flexxi",
    label: "Eligible products mode",
    type: "string",
    defaultValue: "ALL",
    description:
      "ALL = all shop items (within min/max principal). CATEGORIES or PRODUCTS = only IDs listed below; empty lists block everything.",
  },
  {
    key: "flexxi.eligible_category_ids",
    group: "flexxi",
    label: "Eligible category IDs",
    type: "json",
    defaultValue: [],
    description:
      "Used only when mode is CATEGORIES. JSON array of category ids; empty [] means no categories (nothing eligible). Ignored when mode is ALL.",
  },
  {
    key: "flexxi.eligible_product_ids",
    group: "flexxi",
    label: "Eligible product IDs",
    type: "json",
    defaultValue: [],
    description:
      "Used only when mode is PRODUCTS. JSON array of product ids; empty [] means no products (nothing eligible). Ignored when mode is ALL.",
  },
  {
    key: "flexxi.min_principal",
    group: "flexxi",
    label: "Minimum principal (MYR)",
    type: "number",
    defaultValue: 50,
  },
  {
    key: "flexxi.max_principal",
    group: "flexxi",
    label: "Maximum principal (MYR)",
    type: "number",
    defaultValue: 50000,
  },
  {
    key: "flexxi.slot_tiers",
    group: "flexxi",
    label: "Slot tiers",
    type: "json",
    defaultValue: DEFAULT_FLEXXI_SLOT_TIERS,
    description: "JSON array: minScore, maxActivePlans, allowedTemplateCodes.",
  },
  {
    key: "flexxi.reputation_weights",
    group: "flexxi",
    label: "Reputation weights",
    type: "json",
    defaultValue: DEFAULT_FLEXXI_REPUTATION_WEIGHTS,
  },
  {
    key: "flexxi.reminder_days_before",
    group: "flexxi",
    label: "Payment reminder (days before due)",
    type: "number",
    defaultValue: 3,
  },
];

export const SETTING_DEFAULTS: Record<string, unknown> = Object.fromEntries(
  SETTING_DEFS.map((definition) => [definition.key, definition.defaultValue]),
);
